import { Box, Button, buttonClasses, ButtonProps } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Close as CloseIcon } from "@mui/icons-material";
import {
  QuickFilterMenu,
  QuickFilterMenuProps,
} from "../QuickFilterMenu/QuickFilterMenu";

export type QuickFilterButtonProps = ButtonProps & {
  label: string;
  activeCount: number;
  menuOptions?: QuickFilterMenuProps["menuOptions"];
  onMenuOpen?: () => void;
  isLoading?: boolean;
};

export function QuickFilterButton({
  label,
  activeCount,
  menuOptions,
  onMenuOpen,
  sx,
  isLoading,
  ...buttonProps
}: QuickFilterButtonProps) {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  useEffect(() => {
    if (menuIsOpen) {
      onMenuOpen?.();
    }
  }, [menuIsOpen]);

  return (
    <>
      <Button
        ref={buttonRef}
        onClick={() => setMenuIsOpen(!menuIsOpen)}
        variant={menuIsOpen ? "outlined-contained" : "contained"}
        color={menuIsOpen ? "primary" : undefined}
        size="small"
        endIcon={
          <CloseIcon
            sx={{
              transform: `rotate(${menuIsOpen ? 0 : 45}deg)`,
              transition: (theme) => theme.transitions.create("transform"),
            }}
          />
        }
        sx={[
          {
            p: 1,
            border: "1px solid",
            borderColor: (theme) =>
              menuIsOpen ? theme.palette.primary.main : "transparent",
            whiteSpace: "nowrap",
            textTransform: "unset",
            fontSize: "13px",
            lineHeight: "18px",
            fontWeight: 700,
            gap: 1,
            [`.${buttonClasses.endIcon}`]: {
              m: 0,
              svg: { fontSize: "16px" },
            },

            ...(menuIsOpen
              ? { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }
              : {}),
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...buttonProps}
      >
        <Box component="span">
          {label}{" "}
          {activeCount > 0 && (
            <Box component="span" color="text.secondary">
              {activeCount}
            </Box>
          )}
        </Box>
      </Button>
      {buttonRef.current && (
        <QuickFilterMenu
          open={menuIsOpen}
          onClose={() => setMenuIsOpen(false)}
          anchorEl={buttonRef.current}
          menuOptions={menuOptions}
        />
      )}
    </>
  );
}
